import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import NumericSelector from '@components/NumericSelector';
import Select from '@components/Select';
import Text, { TEXT_SIZE } from '@components/Text';
import { VOICE_LOUDNESS_LABEL_MODES } from '../../types';
import { RANGE_MAX_DB, RANGE_MIN_DB } from './audioLevel';
import {
  ConfigLayout,
  Field,
  Fields,
  RangeTable,
  RangeTableTitle,
} from './builderStyles';
import {
  dbfsRangeToSerialRange,
  SERIAL_RANGE_MAX,
  SERIAL_RANGE_MIN,
  serialToHighDbfs,
  serialToLowDbfs,
  VOICE_LOUDNESS_SERIAL_RANGES,
} from './serialScale';

const VoiceLoudnessBuilderConfig = ({ config, onChange }) => {
  const { t } = useTranslation();
  const usesSerialRange = config.labelMode === VOICE_LOUDNESS_LABEL_MODES.SERIAL;
  const {
    lowSerial: targetLowSerial,
    highSerial: targetHighSerial,
  } = dbfsRangeToSerialRange(config.targetLowDbfs, config.targetHighDbfs);
  const labelModeOptions = [
    {
      value: VOICE_LOUDNESS_LABEL_MODES.SERIAL,
      label: t('UI.voiceLoudnessLabelsSerial'),
    },
    {
      value: VOICE_LOUDNESS_LABEL_MODES.EXACT,
      label: t('UI.voiceLoudnessLabelsExact'),
    },
  ];
  const selectedLabelMode = labelModeOptions.find(
    ({ value }) => value === config.labelMode,
  ) || labelModeOptions[0];

  const updateConfig = (field, value) => {
    let nextConfig = { ...config, [field]: value };
    if (field === 'targetLowDbfs') {
      nextConfig = { ...nextConfig, targetLowDbfs: Math.min(value, config.targetHighDbfs) };
    }
    if (field === 'targetHighDbfs') {
      nextConfig = { ...nextConfig, targetHighDbfs: Math.max(value, config.targetLowDbfs) };
    }
    onChange(nextConfig);
  };

  const updateSerialRange = (field, serial) => {
    if (field === 'targetLowDbfs') {
      onChange({
        ...config,
        targetLowDbfs: serialToLowDbfs(serial),
        targetHighDbfs: serialToHighDbfs(Math.max(serial, targetHighSerial)),
      });
      return;
    }
    onChange({
      ...config,
      targetLowDbfs: serialToLowDbfs(Math.min(serial, targetLowSerial)),
      targetHighDbfs: serialToHighDbfs(serial),
    });
  };

  const updateLabelMode = (labelMode) => {
    if (labelMode === VOICE_LOUDNESS_LABEL_MODES.SERIAL) {
      onChange({
        ...config,
        labelMode,
        targetLowDbfs: serialToLowDbfs(targetLowSerial),
        targetHighDbfs: serialToHighDbfs(targetHighSerial),
      });
      return;
    }
    updateConfig('labelMode', labelMode);
  };

  return (
    <ConfigLayout>
      <Fields>
        <Field>
          <Text size={TEXT_SIZE.T4}>
            {t(usesSerialRange
              ? 'UI.voiceLoudnessTargetMinimumSerial'
              : 'UI.voiceLoudnessTargetMinimum')}
          </Text>
          <NumericSelector
            min={usesSerialRange ? SERIAL_RANGE_MIN : RANGE_MIN_DB}
            max={usesSerialRange ? targetHighSerial : config.targetHighDbfs}
            value={usesSerialRange ? targetLowSerial : config.targetLowDbfs}
            onChange={(value) => (usesSerialRange
              ? updateSerialRange('targetLowDbfs', value)
              : updateConfig('targetLowDbfs', value))}
          />
        </Field>
        <Field>
          <Text size={TEXT_SIZE.T4}>
            {t(usesSerialRange
              ? 'UI.voiceLoudnessTargetMaximumSerial'
              : 'UI.voiceLoudnessTargetMaximum')}
          </Text>
          <NumericSelector
            min={usesSerialRange ? targetLowSerial : config.targetLowDbfs}
            max={usesSerialRange ? SERIAL_RANGE_MAX : RANGE_MAX_DB}
            value={usesSerialRange ? targetHighSerial : config.targetHighDbfs}
            onChange={(value) => (usesSerialRange
              ? updateSerialRange('targetHighDbfs', value)
              : updateConfig('targetHighDbfs', value))}
          />
        </Field>
        <Field>
          <Text size={TEXT_SIZE.T4}>{t('UI.voiceLoudnessLabels')}</Text>
          <Select
            value={selectedLabelMode}
            options={labelModeOptions}
            onChange={({ value }) => updateLabelMode(value)}
            width={240}
          />
        </Field>
      </Fields>
      <div>
        <RangeTableTitle size={TEXT_SIZE.T4}>
          {t('UI.voiceLoudnessSerialRangeTable')}
        </RangeTableTitle>
        <RangeTable>
          <thead>
            <tr>
              <th>{t('UI.voiceLoudnessSerial')}</th>
              <th>{t('UI.voiceLoudnessDbfsRange')}</th>
            </tr>
          </thead>
          <tbody>
            {VOICE_LOUDNESS_SERIAL_RANGES.map(({ serial, lowDbfs, highDbfs }) => {
              const isTargetRange = serial >= targetLowSerial
                && serial <= targetHighSerial;
              return (
                <tr
                  aria-selected={isTargetRange}
                  className={isTargetRange ? 'targetRange' : undefined}
                  key={serial}
                >
                  <td>{serial}</td>
                  <td>{`${lowDbfs} - ${highDbfs}`}</td>
                </tr>
              );
            })}
          </tbody>
        </RangeTable>
      </div>
    </ConfigLayout>
  );
};

VoiceLoudnessBuilderConfig.propTypes = {
  config: PropTypes.shape({
    targetLowDbfs: PropTypes.number.isRequired,
    targetHighDbfs: PropTypes.number.isRequired,
    labelMode: PropTypes.string.isRequired,
    trimStepDb: PropTypes.number.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default VoiceLoudnessBuilderConfig;